"use client";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FurnishingType } from "@/db/definitions";
import {
  Blinds,
  Check,
  CookingPot,
  Info,
  LampCeiling,
  Layers,
  Minus,
  Sofa,
} from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";
import { twMerge } from "tailwind-merge";

// Columns run from least to most included.
const levels: Array<FurnishingType> = ["SHELL", "UPHOLSTERED", "FURNISHED"];

// What each level usually includes in Dutch listings; the note says to confirm.
const rows: Array<{
  key: "kitchen" | "floor" | "windows" | "lighting" | "furniture";
  icon: React.ElementType;
  included: Array<FurnishingType>;
}> = [
  { key: "kitchen", icon: CookingPot, included: levels },
  { key: "floor", icon: Layers, included: ["UPHOLSTERED", "FURNISHED"] },
  { key: "windows", icon: Blinds, included: ["UPHOLSTERED", "FURNISHED"] },
  { key: "lighting", icon: LampCeiling, included: ["UPHOLSTERED", "FURNISHED"] },
  { key: "furniture", icon: Sofa, included: ["FURNISHED"] },
];

type Props = {
  /** Button that opens the panel. */
  trigger: React.ReactNode;
  /** Level to mark: the listing's own, or the selected search filter. */
  highlight?: FurnishingType;
  highlightLabel?: "current" | "selected";
};

const FurnishingInfo: React.FC<Props> = ({
  trigger,
  highlight,
  highlightLabel = "current",
}) => {
  const t = useTranslations("listing");

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        collisionPadding={16}
        className="w-[30rem] max-w-[calc(100vw-2rem)] rounded-2xl p-4 shadow-lg sm:p-5"
      >
        <p className="text-sm font-semibold sm:text-base">
          {t("furnishing-info.title")}
        </p>
        <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
          {t("furnishing-info.description")}
        </p>

        {/* Auto layout keeps every column at least as wide as its longest word. */}
        <table className="mt-4 w-full border-separate border-spacing-0 text-xs sm:text-sm">
          <caption className="sr-only">{t("furnishing-info.title")}</caption>
          <thead>
            <tr>
              <th scope="col">
                <span className="sr-only">{t("furnishing-info.item")}</span>
              </th>
              {levels.map((level) => (
                <th
                  key={level}
                  scope="col"
                  className={twMerge(
                    "w-[22%] hyphens-auto rounded-t-xl px-1 pb-2 pt-1.5 text-center align-bottom text-[11px] font-semibold leading-tight sm:text-sm",
                    level === highlight && "bg-secondary",
                  )}
                >
                  {level === highlight && (
                    <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide">
                      {t(`furnishing-info.${highlightLabel}`)}
                    </span>
                  )}
                  {t(`attributes.FURNISHING_TYPE.${level}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.key}>
                <th
                  scope="row"
                  className="border-t border-border py-2 pr-2 text-left font-medium"
                >
                  <span className="flex items-center gap-1.5">
                    <row.icon className="size-4 shrink-0 text-muted-foreground" />
                    {t(`furnishing-info.rows.${row.key}`)}
                  </span>
                </th>
                {levels.map((level) => {
                  const isIncluded = row.included.includes(level);
                  return (
                    <td
                      key={level}
                      className={twMerge(
                        "border-t border-border py-2 text-center",
                        level === highlight && "bg-secondary",
                        level === highlight &&
                          index === rows.length - 1 &&
                          "rounded-b-xl",
                      )}
                    >
                      {isIncluded ? (
                        <Check className="mx-auto size-4" aria-hidden />
                      ) : (
                        <Minus
                          className="mx-auto size-4 text-muted-foreground"
                          aria-hidden
                        />
                      )}
                      <span className="sr-only">
                        {isIncluded
                          ? t("furnishing-info.included")
                          : t("furnishing-info.not-included")}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <p className="mt-4 flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" />
          {t("furnishing-info.note")}
        </p>
      </PopoverContent>
    </Popover>
  );
};

export default FurnishingInfo;
